import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { edificiosApi } from '../../lib/api';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { usePersistentTab } from '../../hooks/usePersistentTab';
import TabParametros from './sections/TabParametros';
import TabPlanCuentas from './sections/TabPlanCuentas';
import TabParticipacion from './sections/TabParticipacion';
import TabPeriodos from './sections/TabPeriodos';
import TabCobros from './sections/TabCobros';
import TabMorosos from './sections/TabMorosos';

export default function ExpensasEdificioDetalle() {
  const { edificioId } = useParams();
  const [edificio, setEdificio] = useState(null);
  const [tab, setTab] = usePersistentTab('tab', 'configuracion');
  const [cfgTab, setCfgTab] = usePersistentTab('cfg', 'plan');
  const [headerSlots, setHeaderSlots] = useState({});

  // Reset edificio + headerSlots cuando cambia el edificio_id desde el sidebar.
  // Esto previene que se vea info stale del edificio anterior durante el fetch
  // (y, junto con el key={edificioId} de los Tab*, fuerza remount limpio de los hijos).
  useEffect(() => {
    setEdificio(null);
    setHeaderSlots({});
    (async () => {
      try { const r = await edificiosApi.get(edificioId); setEdificio(r.data); } catch {}
    })();
  }, [edificioId]);

  // IMPORTANTE: `registerHeader(tabId)` debe devolver SIEMPRE la misma referencia
  // de función por tabId; de lo contrario los hijos disparan su `useEffect([registerHeader])`
  // en cada render y caemos en un loop "Maximum update depth exceeded" (que congela
  // la reconciliación de React al cambiar de edificio, dejando datos stale en pantalla).
  const headerSettersRef = useRef({});
  const registerHeader = React.useCallback((tabId) => {
    if (!headerSettersRef.current[tabId]) {
      headerSettersRef.current[tabId] = (node) => {
        setHeaderSlots((prev) => (prev[tabId] === node ? prev : { ...prev, [tabId]: node }));
      };
    }
    return headerSettersRef.current[tabId];
  }, []);

  const initials = (edificio?.nombre || '...')
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 3);

  // El slot del header se renderiza arriba para Liquidación/Cobros, y junto a las
  // sub-tabs cuando estás en Configuración (para que el botón Editar / buscador
  // queden alineados con Plan / Participación / Parámetros).
  const topHeaderSlot = tab !== 'configuracion' ? headerSlots[tab] : null;
  const cfgHeaderSlot = headerSlots[cfgTab];

  return (
    <div className="p-3 md:p-4 max-w-[1500px] mx-auto">
      <Tabs value={tab} onValueChange={setTab}>
        <div className="flex items-center justify-between gap-4 mb-3 border-b border-slate-200 pb-2">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-indigo-100 text-indigo-700 flex items-center justify-center font-bold text-sm" title={edificio?.nombre}>
              {initials}
            </div>
            <TabsList className="bg-transparent p-0 h-auto gap-1">
              <TabsTrigger value="configuracion" data-testid="tab-configuracion" className="data-[state=active]:bg-slate-100">
                <span className="text-slate-400 mr-1.5 text-xs">1</span> Configuración
              </TabsTrigger>
              <TabsTrigger value="periodos" data-testid="tab-periodos" className="data-[state=active]:bg-slate-100">
                <span className="text-slate-400 mr-1.5 text-xs">2</span> Liquidación mensual
              </TabsTrigger>
              <TabsTrigger value="cobros" data-testid="tab-cobros" className="data-[state=active]:bg-slate-100">
                <span className="text-slate-400 mr-1.5 text-xs">3</span> Cobros
              </TabsTrigger>
              <TabsTrigger value="morosos" data-testid="tab-morosos" className="data-[state=active]:bg-slate-100">
                <span className="text-slate-400 mr-1.5 text-xs">4</span> Morosos
              </TabsTrigger>
            </TabsList>
          </div>
          <div className="flex items-center gap-2 flex-1 justify-end min-w-0">
            {topHeaderSlot}
          </div>
        </div>

        <TabsContent value="configuracion">
          <Tabs value={cfgTab} onValueChange={setCfgTab}>
            <div className="flex items-center justify-between gap-3 mb-3">
              <TabsList className="bg-slate-50 border border-slate-200 p-0.5 h-auto gap-1">
                <TabsTrigger value="plan" data-testid="tab-plan" className="data-[state=active]:bg-white text-xs">
                  <span className="text-slate-400 mr-1.5 text-[10px]">1</span> Plan de cuentas
                </TabsTrigger>
                <TabsTrigger value="participacion" data-testid="tab-part" className="data-[state=active]:bg-white text-xs">
                  <span className="text-slate-400 mr-1.5 text-[10px]">2</span> Participación
                </TabsTrigger>
                <TabsTrigger value="parametros" data-testid="tab-param" className="data-[state=active]:bg-white text-xs">
                  <span className="text-slate-400 mr-1.5 text-[10px]">3</span> Parámetros
                </TabsTrigger>
              </TabsList>
              <div className="flex items-center gap-2 flex-1 justify-end min-w-0">
                {cfgHeaderSlot}
              </div>
            </div>
            <TabsContent value="plan"><TabPlanCuentas key={edificioId} edificioId={edificioId} registerHeader={registerHeader('plan')} /></TabsContent>
            <TabsContent value="participacion"><TabParticipacion key={edificioId} edificioId={edificioId} registerHeader={registerHeader('participacion')} /></TabsContent>
            <TabsContent value="parametros"><TabParametros key={edificioId} edificioId={edificioId} registerHeader={registerHeader('parametros')} /></TabsContent>
          </Tabs>
        </TabsContent>

        <TabsContent value="periodos"><TabPeriodos key={edificioId} edificioId={edificioId} registerHeader={registerHeader('periodos')} /></TabsContent>
        <TabsContent value="cobros"><TabCobros key={edificioId} edificioId={edificioId} registerHeader={registerHeader('cobros')} /></TabsContent>
        <TabsContent value="morosos"><TabMorosos key={edificioId} edificioId={edificioId} registerHeader={registerHeader('morosos')} /></TabsContent>
      </Tabs>
    </div>
  );
}
